/**
 * ChatNavigateChip — tappable navigation CTA rendered when the backend
 * emits a `navigate_to` SSE event.
 *
 * AI Chat V1.1 Item 6 (DR design session 2026-05-04). Sits in the same
 * slot as ChatGapChip (below the last assistant message). Clicking
 * navigates to the destination, fires a follow_up_clicked telemetry
 * event, and clears the pending navigation. Unlike ChatGapChip, it
 * does NOT pause the agent loop — the prose answer is already complete.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMobile } from '../../../hooks/useMobile';
import { useChatStore, type NavigationCTA } from '../../../stores/chatStore';
import { useChatTelemetry } from '../hooks/useChatTelemetry';

// V1.3: `upload` and `deal_upload` destinations were retired — file upload
// flows through the inline ChatUploadCard now. navigate_to is reserved for
// views that need the full app viewport.
const ROUTE_MAP: Record<NavigationCTA['destination'], (args: NavigationCTA) => string | null> = {
  deals_list: () => '/deals',
  deal_detail: ({ deal_id }) => (deal_id ? `/deals/${deal_id}` : null),
  analysis: ({ deal_id }) => (deal_id ? `/deals/${deal_id}/analysis` : null),
};

export function ChatNavigateChip() {
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { logEvent } = useChatTelemetry();
  const nav = useChatStore((s) => s.pendingNavigation);
  const clearPendingNavigation = useChatStore((s) => s.clearPendingNavigation);

  // On mobile the destination pages don't exist (MobileChatGate redirects
  // them back to /chat), so any pending navigation is a dead chip. Clear
  // it from the store via an effect — clearing during render would skip
  // follow-up chips that gate on `!pendingNavigation` in the same tick.
  useEffect(() => {
    if (isMobile && nav) {
      clearPendingNavigation();
    }
  }, [isMobile, nav, clearPendingNavigation]);

  if (!nav) {return null;}
  if (isMobile) {return null;}

  const path = ROUTE_MAP[nav.destination]?.(nav);

  const handleClick = () => {
    logEvent('follow_up_clicked', {
      chip_id: `navigate_to_${nav.destination}`,
      tool_name: 'navigate_to',
      position: 0,
    });
    clearPendingNavigation();
    if (path) {navigate(path);}
  };

  // If the destination requires a deal_id but none was provided, drop the
  // chip silently rather than rendering a dead button.
  if (!path) {return null;}

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleClick}
        className="border border-orange bg-surface-card px-3 py-2 text-body-sm text-orange transition-colors hover:bg-orange hover:text-surface-card"
      >
        → {nav.label}
      </button>
    </div>
  );
}
